import React, { useCallback, useEffect } from 'react';
import { isMobile } from '@hud/web-components/utils';
import { useLocation } from 'react-router-dom';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import useAnalytics from '../../hooks/useAnalytics';
import { Wizard } from '@hud/web-components/Wizard/Wizard';
import { CompanyPreset, CreateAccountStep } from './Steps/CreateAccount';
import { useAuth } from '../../hooks/useAuth';
import useAccountInfo from '../../hooks/useAccountInfo';
import { ThemeProvider } from 'styled-components';
import { darkColors } from '@hud/web-components/theme/colors';
import ClientProvider from '../../hooks/useClient';
import { useConfig } from '../../hooks/useConfig';
import usePlugSDK from '../../hooks/usePlugSDK';
import InstallationSetupStepContent from './Steps/InstallationSetupStepContent';

interface AuthenticatedContentProps {
  user:
    | {
        email?: string;
      }
    | undefined;
  initialCompany?: CompanyPreset;
  onLogout: () => void;
}

const installSDKMobile = <>
  <p>Switch to the Desktop version to continue install Hud SDK.</p>
  <p><a href="https://docs.hud.io/docs/nodejs-installation-guide" target="_blank" rel="noopener noreferrer">Hud SDK installation guide</a></p>
</>

// Create a separate component that uses hooks dependent on ClientProvider
const AuthenticatedContent = ({
  user,
  initialCompany,
  onLogout,
}: AuthenticatedContentProps) => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const domain = user?.email?.split('@')[1];
  const { data: accountInfoData, refetch: refetchAccountInfo } = useAccountInfo();
  const companyData = initialCompany || {
    name: accountInfoData?.companyName || '',
    domain: domain || '',
    tenancy: accountInfoData?.tenancy,
  };

  useEffect(() => {
    if (accountInfoData && accountInfoData.companyName) {
      setCurrentStep(1);
    }
  }, [accountInfoData]);

  const handleStepComplete = useCallback(() => {
    // Refresh account info to get the updated companyName
    refetchAccountInfo();
    setCurrentStep(1);
  }, [refetchAccountInfo]);

  return (
    <ThemeProvider theme={darkColors}>
      <MantineProvider forceColorScheme={'dark'}>
        <Notifications zIndex={1001} />
        <Wizard
          title="Setup Guide"
          opened={true}
          options={{
            showLogo: true,
            withClickOutsideToClose: true,
          }}
          currentStep={currentStep}
          wizardSteps={[
            {
              step: { title: 'Create account', icon: 'other.user' },
              content: (
                <CreateAccountStep
                  initialCompany={companyData}
                  onSave={handleStepComplete}
                />
              ),
            },
            {
              step: {
                title: 'Install SDK',
                icon: 'other.gift',
                ...( isMobile() ? {
                  description: installSDKMobile,
                  isTall: true
                } : {})
              },
              content: <InstallationSetupStepContent />,
            },
            {
              step: {
                title: 'Install MCP server',
                icon: 'other.mcp',
                description: 'Optional',
              },
              content: <div></div>,
            },
            {
              step: {
                title: 'Run in production',
                icon: 'other.rocket',
                description: 'Change lives',
              },
              content: <div></div>,
            },
          ]}
          onLogout={onLogout}
        />
      </MantineProvider>
    </ThemeProvider>
  );
};

interface SetupPageProps {
  initialCompany?: CompanyPreset;
}

function SetupPage({ initialCompany }: SetupPageProps) {
  const { user, isLoading, token, logout } = useAuth();
  const config = useConfig();
  usePlugSDK();
  const location = useLocation();
  const analytics = useAnalytics();

  useEffect(() => {
    document.title = 'Hud';
  }, []);

  useEffect(() => {
    analytics.pageview();
  }, [analytics, location.pathname]);

  return isLoading || !token ? ( // prevent flashing
    <ThemeProvider theme={darkColors}>
      <div></div>
    </ThemeProvider>
  ) : (
    <ClientProvider
      url={config.API_BASE_URL}
      detectionServiceUrl={config.DETECTION_SERVICE_URL}
      token={token}
    >
      <AuthenticatedContent
        user={user}
        initialCompany={initialCompany}
        onLogout={logout}
      />
    </ClientProvider>
  );
}
export default SetupPage;
